import * as React from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { MarkdownView } from "@/components/console/MarkdownView"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

export function RecordEdit({ token }: { token: string }) {
  const { audience: pathAudience, id } = useParams()
  const [params] = useSearchParams()
  const isNew = id === undefined
  const audience = pathAudience ?? params.get("audience") ?? ""

  const existing = useQuery({
    queryKey: ["record", audience, id, token],
    queryFn: () => api.record(token, audience, id as string),
    enabled: !isNew,
  })

  if (!isNew && existing.isPending) return <PageSkeleton />
  if (!isNew && existing.isError)
    return <FetchError error={existing.error} retry={() => existing.refetch()} />

  // Past this point, existing.data is guaranteed whenever !isNew (the two
  // early returns above already gate on isPending/isError). RecordForm is
  // keyed on the record identity, so a fresh mount always gets a correct,
  // current snapshot as its initial state — no effect required to keep it
  // "in sync" with a query object that may go on to refetch in the background.
  return (
    <RecordForm
      key={id ?? "new"}
      isNew={isNew}
      audience={audience}
      id={id}
      token={token}
      initialTitle={existing.data?.record.title ?? ""}
      initialContent={existing.data?.record.content ?? ""}
      initialEtag={existing.data?.etag ?? ""}
    />
  )
}

type RecordFormProps = {
  isNew: boolean
  audience: string
  id: string | undefined
  token: string
  initialTitle: string
  initialContent: string
  initialEtag: string
}

function RecordForm({
  isNew,
  audience,
  id,
  token,
  initialTitle,
  initialContent,
  initialEtag,
}: RecordFormProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()

  // These read their starting value once, at this component instance's
  // first render, via useState's initializer. That's it — there's no effect
  // watching the parent's query data, so a background refetch (e.g. the
  // record tab regaining focus after staleTime elapses) can never reach in
  // and overwrite in-progress edits or the etag this form will save with.
  // A genuinely different record gets a genuinely new RecordForm instance
  // (see the `key` in RecordEdit above), which is the only time these
  // should reset.
  const [title, setTitle] = React.useState(initialTitle)
  const [content, setContent] = React.useState(initialContent)
  const [etag] = React.useState(initialEtag)
  const [preview, setPreview] = React.useState(false)
  const [conflict, setConflict] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return api.createRecord(token, { title, content, audience })
      }
      return api.editRecord(token, audience, id as string, { title, content }, etag)
    },
    onSuccess: (result) => {
      setConflict(false)
      setSaveError(null)
      qc.invalidateQueries({ queryKey: ["records", audience] })
      qc.invalidateQueries({ queryKey: ["record", audience, result.record.id] })
      navigate(`/records/${audience}/${result.record.id}`)
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409) {
        setConflict(true)
        setSaveError(null)
      } else {
        setSaveError(error.message)
        setConflict(false)
      }
    },
  })

  return (
    <SectionCard title={isNew ? "new record" : "edit record"}>
      {conflict ? (
        <p className="mb-3 font-mono text-xs" style={{ color: "var(--viz-failed)" }}>
          This record changed since you loaded it. Reload before saving again.
        </p>
      ) : null}
      {saveError ? (
        <p className="mb-3 font-mono text-xs" style={{ color: "var(--viz-failed)" }}>
          {saveError}
        </p>
      ) : null}
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <div>
          <Label htmlFor="title">title</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="content">content (Markdown)</Label>
            <button
              type="button"
              onClick={() => setPreview((p) => !p)}
              className="font-mono text-xs text-primary hover:underline"
            >
              {preview ? "edit" : "preview"}
            </button>
          </div>
          {preview ? (
            <div className="border border-border bg-card p-3">
              <MarkdownView content={content} audience={audience} />
            </div>
          ) : (
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={16}
              required
              className="w-full border border-input bg-background p-2 font-mono text-xs"
            />
          )}
        </div>
        <Button type="submit" disabled={save.isPending}>
          save
        </Button>
      </form>
    </SectionCard>
  )
}
